import { useMemo } from "react";
import {
  formatInternationalHomeVipPriceDisplay,
  resolveInternationalHomeVipCheckoutPricing,
  type InternationalHomeVipCheckoutPricing,
} from "@/lib/resolveInternationalHomeOffer";
import type { InternationalHomeVipPricingByCountry } from "@/lib/internationalHomeTemplate";

export type UseInternationalHomeVipCheckoutPricingOpts = {
  country?: string | null;
  vipPricingByCountry?: InternationalHomeVipPricingByCountry | null;
  /** Compact default from public site-settings (all countries same offer). */
  vipPricingDefault?: import("@/lib/internationalHomeTemplate").InternationalHomeVipPricing | null;
  runtimeVipPriceUsd?: number | null;
  language?: string;
  defaultProductName?: string;
  /** Formats monthly price for CTA/drawer copy. */
  pricePerMonth?: (formatted: string) => string;
};

export type UseInternationalHomeVipCheckoutPricingResult = {
  pricing: InternationalHomeVipCheckoutPricing;
  priceDisplay: {
    originalPriceLine: string | null;
    priceLine: string;
    priceSubline: string | null;
  };
};

export function useInternationalHomeVipCheckoutPricing(
  opts: UseInternationalHomeVipCheckoutPricingOpts,
): UseInternationalHomeVipCheckoutPricingResult {
  const pricePerMonth =
    opts.pricePerMonth ??
    ((formatted: string) => `${formatted} / month`);

  const pricing = useMemo(
    () =>
      resolveInternationalHomeVipCheckoutPricing({
        country: opts.country,
        vipPricingByCountry: opts.vipPricingByCountry,
        vipPricingDefault: opts.vipPricingDefault,
        runtimeVipPriceUsd: opts.runtimeVipPriceUsd,
        language: opts.language,
        defaultProductName: opts.defaultProductName,
      }),
    [
      opts.country,
      opts.vipPricingByCountry,
      opts.vipPricingDefault,
      opts.runtimeVipPriceUsd,
      opts.language,
      opts.defaultProductName,
    ],
  );

  const priceDisplay = useMemo(
    () =>
      formatInternationalHomeVipPriceDisplay({
        pricing,
        pricePerMonth,
        language: opts.language,
      }),
    [pricing, pricePerMonth, opts.language],
  );

  return { pricing, priceDisplay };
}
